import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status) || attempts.current >= 10) {
          setState("failed");
          return;
        }
      } catch (_) {
        if (attempts.current >= 10) {
          setState("failed");
          return;
        }
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center space-y-6" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-[#0044FF] mx-auto" />
          <h1 className="font-heading font-black text-2xl">Verifica del pagamento…</h1>
          <p className="text-sm text-slate-500">Attendi qualche secondo, stiamo confermando con Stripe.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
          <h1 className="font-heading font-black text-2xl">Abbonamento attivo! 🎉</h1>
          <p className="text-sm text-slate-500">
            Grazie! Il piano è stato attivato per la tua azienda. Buon lavoro con IL-Report.
          </p>
          <Button
            onClick={() => navigate("/home")}
            data-testid="payment-success-home-button"
            className="h-12 px-8 font-bold bg-[#0044FF] hover:bg-[#0033CC]"
          >
            Vai alla Home
          </Button>
        </>
      )}
      {state === "failed" && (
        <>
          <AlertTriangle className="h-14 w-14 text-amber-500 mx-auto" />
          <h1 className="font-heading font-black text-2xl">Pagamento non confermato</h1>
          <p className="text-sm text-slate-500">
            Non abbiamo ricevuto la conferma. Se hai completato il pagamento, riprova tra
            qualche minuto o contattaci.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate("/abbonamento")}
            data-testid="payment-failed-retry-button"
            className="h-12 px-8 font-bold"
          >
            Torna all'abbonamento
          </Button>
        </>
      )}
    </div>
  );
}
